import React, { useMemo, useState, useCallback, useEffect, memo } from 'react';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import PageHeader from '../../components/PageHeader';
import AddButton from '../../components/AddButton';
import ActionToolbar from '../../components/ActionToolbar';
import useGet from '../../hooks/useGet';
import DataList from '../../components/DataList';
import EmptyTableIndicator from '../../components/EmptyTableIndicator';
import useDelete from '../../hooks/useDelete';
import useCreate from '../../hooks/useCreate';
import useUpdate from '../../hooks/useUpdate';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import MaterialModal from './MaterialModal';
import { getConstitutionsTableConfig, PAGE_CONFIG } from './config';
import api from '../../api';

const MaterialTypes = () => {
    const { t } = useTranslation();
    const tr = useCallback((key) => t(`materialTypes:${key}`), [t]);

    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        search: ''
    });
    const [searchQuery, setSearchQuery] = useState('');
    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage < 1) return;
        setParams((prev) => ({ ...prev, page: newPage }));
    };

    const handlePageSizeChange = (limit) => {
        if (limit < 1) return;
        setParams((prev) => ({ ...prev, limit }));
    };

    const { data, pagination, isLoading, refetch } = useGet(
        PAGE_CONFIG.GET_QUERY_KEY,
        () => api.materialTypes.getMaterialTypes(params), // Removed useMemo
        PAGE_CONFIG.DEFAULT_ERROR_MESSAGE,
        PAGE_CONFIG.SELECT,
        params
    );

    const { deleteItem, isLoading: deleteLoading, deleteDialogProps } = useDelete(PAGE_CONFIG.GET_QUERY_KEY, PAGE_CONFIG.DELETE_API_CALL);
    const { create, status: createStatus, createDialogProps } = useCreate(PAGE_CONFIG.GET_QUERY_KEY, PAGE_CONFIG.CREATE_API_CALL, tr('createSuccessMessage'), tr('createErrorMessage'));
    const { update, status: updateStatus, updateDialogProps } = useUpdate(PAGE_CONFIG.GET_QUERY_KEY, PAGE_CONFIG.UPDATE_API_CALL, tr('updateSuccessMessage'), tr('updateErrorMessage'));

    const handleEdit = useCallback((item) => update(item), [update]);
    const handleDelete = useCallback((id) => deleteItem(id), [deleteItem]);
    const columns = useMemo(() => getConstitutionsTableConfig( handleEdit, handleDelete, tr), [ handleEdit, handleDelete, tr]);
    // Create a debounced search function
    const debouncedSearch = useMemo(
        () =>
            debounce((searchValue) => {
                setParams((prev) => ({ ...prev, search: searchValue, page: 1 }));
            }, 500),
        []
    );

    // Cleanup debounce on component unmount
    useEffect(() => {
        return () => debouncedSearch.cancel();
    }, [debouncedSearch]);

    // Trigger refetch when params change
    useEffect(() => {
        refetch();
    }, [params, refetch]);

    const handleSearch = (value) => {
        setSearchQuery(value);
        debouncedSearch(value);
    };

    return (
        <>
            <div className="flex">
                <div className="flex-1 p-4">
                    <PageHeader items={PAGE_CONFIG.BREADCRUMB} pageTitle={PAGE_CONFIG.PAGE_TITLE} />
                    <ActionToolbar searchQuery={searchQuery} onSearchChange={handleSearch} actionButton={<AddButton isLoading={false} onClick={createDialogProps?.onOpen} label={tr('create')} />} disabled={false} />
                    {data?.length > 0 ? (
                        <DataList data={data} columns={columns} pagination={pagination} onPageChange={handlePageChange} onPageSizeChange={handlePageSizeChange} />
                    ) : (
                        <EmptyTableIndicator colSpan={5} isLoading={isLoading} icon={faSearch} description={tr('searchDescription')} placeholder={tr('searchPlaceholder')} loadingMessage={tr('searchLoadingMessage')} />
                    )}
                </div>
            </div>
            <MaterialModal tr={tr} onSubmit={create} isOpen={createDialogProps?.isOpen} onClose={createDialogProps?.onClose} status={createStatus} />
            <ConfirmationDialog tr={tr} isLoading={deleteLoading} {...deleteDialogProps} />
            <MaterialModal tr={tr} {...updateDialogProps} isOpen={updateDialogProps?.isOpen} onClose={updateDialogProps?.onClose} status={updateStatus} />
        </>
    );
};

export default memo(MaterialTypes);
